import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  err: Error | null
  info?: ErrorInfo
}

/**
 * Catches render-time throws anywhere below it. Without this, an
 * unhandled error during render unmounts the React root and the
 * user sees the page background ("blue-screen of death") with no
 * way to recover except F5. With this, we render the error + a
 * recovery button.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { err: null }
  }

  static getDerivedStateFromError(err: Error): State {
    return { err }
  }

  componentDidCatch(err: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[bskyhq] render error', err, info)
    this.setState({ err, info })
  }

  reset = () => this.setState({ err: null, info: undefined })

  render() {
    if (this.state.err) {
      return (
        <div className="mx-auto mt-10 max-w-2xl px-4">
          <div className="glass-tinted p-4 text-sm">
            <h2 className="mb-2 text-base font-bold text-rose-200">
              💥 something blew up while rendering
            </h2>
            <p className="text-xs text-ink-200">
              An error escaped a component and React unmounted the tree to
              keep things consistent. The cache is still in IndexedDB; this
              is just a render-side crash.
            </p>
            <pre className="mt-3 max-h-60 overflow-auto rounded-xl border border-rose-400/30 bg-rose-500/10 p-2 text-[11px] text-rose-100">
              {String(this.state.err?.stack ?? this.state.err?.message ?? this.state.err)}
            </pre>
            {this.state.info?.componentStack ? (
              <details className="mt-2 text-[10px] text-ink-400">
                <summary className="cursor-pointer">component stack</summary>
                <pre className="mt-1 max-h-60 overflow-auto whitespace-pre-wrap break-all">
                  {this.state.info.componentStack}
                </pre>
              </details>
            ) : null}
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                className="glass-button text-xs"
                onClick={this.reset}
              >
                ↻ try again
              </button>
              <button
                type="button"
                className="glass-button text-xs"
                onClick={() => location.reload()}
              >
                ⟳ reload page
              </button>
              <button
                type="button"
                className="glass-button text-xs"
                onClick={() => location.assign('/')}
              >
                ← home
              </button>
            </div>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
